import { t } from 'i18next';
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isInventoryAuditorContext, isTomsVendingContext } from '@/constants/business-units';
import { useAuthData } from '@/hooks/getAuthData';
import { isReportOnlyUser } from '@/utils/roles';
import TomsVendingDashboardPage from './TomsVendingDashboardPage';
import InventoryAuditorDashboardPage from './InventoryAuditorDashboardPage';
import { Navigate } from 'react-router-dom';
import { useAllGoaProfitRolling } from '@/hooks/api/useGoas';
import LineGraph from '../graph/LineGraph';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import PieChart from '../graph/PieChart';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Box, Grid, Typography, Card, CardContent, List, ListItem, ListItemText, Divider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DashboardTitleBar } from '../layout/DashboardTitleBar';
import Loading from '../core/Loading';
import RefreshIcon from '@mui/icons-material/Refresh';
import StatCard from '../dashboard/StatCard';
import { fetchAllLocations } from '@/hooks/useLocation';
import { useOutages } from '@/hooks/api/useOutages';
import { useRejects } from '@/hooks/api/useRejects';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import WarningIcon from '@mui/icons-material/Warning';
import HttpClient from '@/api/HttpClient';
import { useQueryLocationVisits } from '@/hooks/api/useLocationVisits';

const httpClient = new HttpClient();

function SouthernMintDashboard(props: any) {
    const { user } = useAuth();
    const [rollingDailyProfit, setRollingDailyProfit] = useState<any[]>([]);
    const [byGoaId, setbyGoaId] = useState(null as any);
    const [byLocation, setByLocation] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [locationVisits, setLocationVisits] = useState<any[]>([]);
    const [dashboardLoading, setDashboardLoading] = useState(false);

    const { data: outagesData } = useOutages();
    const { data: rejectsData } = useRejects();
    const queryLocationVisitsMutation = useQueryLocationVisits();
    
    const outages = Array.isArray(outagesData) ? outagesData : (outagesData ? Object.values(outagesData) : []);
    const rejects = Array.isArray(rejectsData) ? rejectsData : (rejectsData ? Object.values(rejectsData) : []);
    
    const startDateRef = React.useRef<Dayjs | null>(dayjs().subtract(1, 'year').startOf('day'));
    const endDateRef = React.useRef<Dayjs | null>(dayjs().startOf('day'));
    
    const profitRollingParams = useMemo(() => ({
        startDate: startDateRef.current?.format('YYYY-MM-DD'),
        endDate: endDateRef.current?.format('YYYY-MM-DD'),
    }), []);
    
    const { data: profitRollingData, isLoading: loading, refetch: refetchProfitRolling } = useAllGoaProfitRolling(profitRollingParams);

    useEffect(() => {
        if (profitRollingData) {
            if (profitRollingData?.totalPerDay) {
                setRollingDailyProfit(profitRollingData.totalPerDay);
            }
            if (profitRollingData?.byGoaId) {
                setbyGoaId(profitRollingData?.byGoaId);
            }
            if (profitRollingData?.byLocation) {
                setByLocation(profitRollingData?.byLocation);
            }
        }
    }, [profitRollingData]);
    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setDashboardLoading(true);
        try {
            // Fetch locations
            const locationsData = await fetchAllLocations();
            setLocations(locationsData);

            // Fetch recent location visits
            try {
                queryLocationVisitsMutation.mutate({
                    skip: 0,
                    take: 10,
                    search: '',
                    orderBy: 'date',
                    orderDirection: 'DESC',
                }, {
                    onSuccess: (data: any) => {
                        setLocationVisits(data?.data || []);
                    },
                });
            } catch (error) {
                console.error('Error fetching location visits:', error);
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setDashboardLoading(false);
        }
    };

    const preparePieChartData = (byGoaId: Record<number, any[]>) => {
        const labels: string[] = Object.keys(byLocation).map((x) => x);
        const values: number[] = Object.values(byLocation).map((x) => x);

        for (let i = 0; i < labels.length; i++) {
            labels[i] = `($${values[i].toLocaleString()}): ${labels[i]}`;
        }

        return {
            labels,
            datasets: [
                {
                    label: 'Total Net Gain by GOA',
                    data: values,
                    backgroundColor: [
                        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
                        '#FF9F40', '#C9CBCF', '#FF6666', '#66CC99', '#6699FF'
                    ]
                }
            ]
        };
    };

    const computeYBoundsWithoutOutliers = (values: number[]) => {
        const sorted = [...values].sort((a, b) => a - b);

        const q1Index = Math.floor(sorted.length * 0.03);
        const q3Index = Math.floor(sorted.length * 0.97);

        const q1 = sorted[q1Index];
        const q3 = sorted[q3Index];
        const iqr = q3 - q1;

        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;

        // Filter values within bounds and get min/max of that filtered set
        const filtered = sorted.filter(v => v >= lowerBound && v <= upperBound);

        const min = Math.min(...filtered);
        const max = Math.max(...filtered);

        return { min, max };
    };

    const prepareChartData = (data: { date: string, netGain: number }[]) => {
        const values = data.map((item) => item.netGain);
        const { min, max } = computeYBoundsWithoutOutliers(values);

        return {
            labels: data.map((item) => dayjs(item.date).format('MM/DD/YYYY')),
            datasets: [
                {
                    label: 'Net Gain Per Day (All GOAs)',
                    data: values,
                    borderColor: 'rgb(255, 159, 64)',
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    tension: 0.2,
                    fill: true,
                }
            ],
            yBounds: { min, max } // pass it out if needed
        };
    };

    const chartData = React.useMemo(() => {
        return prepareChartData(rollingDailyProfit);
    }, [rollingDailyProfit]);

    const renderRollingAverageGraph = () => {
        if (!rollingDailyProfit || rollingDailyProfit.length === 0) return null;

        return (
            <div style={{ height: '400px', width: '100%' }}>
                <LineGraph
                    chartData={chartData}
                    uuid="netGainPerDayGraph"
                    title="Net Gain Per Day Over Time"
                    height={400}
                    width={600}
                    isCurrency={true}
                    minYValue={Math.round(chartData.yBounds.min)}
                    maxYValue={Math.round(chartData.yBounds.max)}
                />
            </div>
        );
    };

    const pieChartData = React.useMemo(() => {
        return preparePieChartData(byGoaId);
    }, [byGoaId]);

    const renderGoaPieChart = () => {
        if (!byGoaId) return null;

        return (
            <PieChart
                uuid="profitDistribution"
                chartData={pieChartData}
                title="Profit Distribution by GOA"
                width={400}
                height={400}
                showLabels={false}
            />
        );
    };

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Dayjs | null>>, newValue: Dayjs | null) => {
        setter(newValue);
    };

    // Calculate statistics
    const stats = useMemo(() => {
        const totalLocations = locations.length;
        
        const totalAssets = locations.reduce((sum, loc) => {
            return sum + Number(loc.goaCount || 0) + Number(loc.trkCount || 0) + Number(loc.atmCount || 0) + Number(loc.ctmCount || 0);
        }, 0);

        const assetBreakdown = locations.reduce((acc, loc) => {
            acc.goa += Number(loc.goaCount || 0);
            acc.trk += Number(loc.trkCount || 0);
            acc.atm += Number(loc.atmCount || 0);
            acc.ctm += Number(loc.ctmCount || 0);
            return acc;
        }, { goa: 0, trk: 0, atm: 0, ctm: 0 });

        // Calculate total revenue from profit data
        const totalRevenue = rollingDailyProfit.reduce((sum, day) => sum + (day.netGain || 0), 0);
        
        // Calculate period comparison (this month vs last month)
        const now = dayjs();
        const thisMonthStart = now.startOf('month');
        const lastMonthStart = now.subtract(1, 'month').startOf('month');
        const lastMonthEnd = now.subtract(1, 'month').endOf('month');
        
        const thisMonthRevenue = rollingDailyProfit
            .filter(day => dayjs(day.date).isAfter(thisMonthStart) || dayjs(day.date).isSame(thisMonthStart, 'day'))
            .reduce((sum, day) => sum + (day.netGain || 0), 0);
        
        const lastMonthRevenue = rollingDailyProfit
            .filter(day => {
                const dayDate = dayjs(day.date);
                return (dayDate.isAfter(lastMonthStart) || dayDate.isSame(lastMonthStart, 'day')) &&
                       (dayDate.isBefore(lastMonthEnd) || dayDate.isSame(lastMonthEnd, 'day'));
            })
            .reduce((sum, day) => sum + (day.netGain || 0), 0);
        
        const revenueChange = lastMonthRevenue > 0 
            ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
            : 0;

        // Recent visits (last 7 days)
        const sevenDaysAgo = dayjs().subtract(7, 'days');
        const recentVisits = locationVisits.filter(visit => {
            const visitDate = dayjs(visit.date);
            return visitDate.isAfter(sevenDaysAgo) || visitDate.isSame(sevenDaysAgo, 'day');
        }).length;

        // Active outages (unresolved)
        const activeOutages = outages.filter((outage: any) => !outage.resolved).length;

        // Average daily revenue
        const daysWithData = rollingDailyProfit.length;
        const avgDailyRevenue = daysWithData > 0 ? totalRevenue / daysWithData : 0;

        // Top location by revenue
        // byLocation is an object with location names as keys and revenue as values
        const locationRevenueMap: Record<string, number> = {};
        if (byLocation && typeof byLocation === 'object' && !Array.isArray(byLocation)) {
            Object.keys(byLocation).forEach(locationName => {
                locationRevenueMap[locationName] = byLocation[locationName];
            });
        }
        const topLocation = Object.entries(locationRevenueMap).length > 0
            ? Object.entries(locationRevenueMap).sort(([, a], [, b]) => b - a)[0]
            : null;

        // Top 5 locations
        const topLocations = Object.entries(locationRevenueMap)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([name, revenue]) => ({ name, revenue }));

        return {
            totalLocations,
            totalAssets,
            assetBreakdown,
            totalRevenue,
            revenueChange,
            recentVisits,
            activeOutages,
            avgDailyRevenue,
            topLocation: topLocation ? { name: topLocation[0], revenue: topLocation[1] } : null,
            topLocations,
        };
    }, [locations, rollingDailyProfit, locationVisits, outages, byLocation]);

    // Prepare asset distribution chart data
    const assetDistributionData = useMemo(() => {
        if (!stats.assetBreakdown) return null;
        
        return {
            labels: ['GOA', 'TRK', 'ATM', 'CTM'],
            datasets: [
                {
                    label: 'Asset Distribution',
                    data: [
                        stats.assetBreakdown.goa,
                        stats.assetBreakdown.trk,
                        stats.assetBreakdown.atm,
                        stats.assetBreakdown.ctm,
                    ],
                    backgroundColor: [
                        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'
                    ]
                }
            ]
        };
    }, [stats.assetBreakdown]);

    const renderAssetDistributionChart = () => {
        if (!assetDistributionData) return null;

        return (
            <PieChart
                uuid="assetDistribution"
                chartData={assetDistributionData}
                title="Asset Distribution"
                width={400}
                height={400}
            />
        );
    };

    const dateFilterRow = (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2} marginBottom={3} flexWrap="wrap" alignItems="center">
                <DatePicker
                    label="Start Date"
                    defaultValue={startDateRef.current}
                    onChange={(newValue) => {
                        startDateRef.current = dayjs.isDayjs(newValue) ? newValue : null;
                    }}
                    slotProps={{ textField: { fullWidth: false } }}
                />
                <DatePicker
                    label="End Date"
                    defaultValue={endDateRef.current}
                    onChange={(newValue) => {
                        endDateRef.current = dayjs.isDayjs(newValue) ? newValue : null;
                    }}
                    slotProps={{ textField: { fullWidth: false } }}
                />

                {loading || dashboardLoading ? (
                    <Box
                        sx={{
                            borderRadius: '50%',
                            backgroundColor: '#e0e0e0',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' },
                            },
                        }}
                    >
                        <RefreshIcon />
                    </Box>
                ) : (
                    <Box
                        onClick={() => {
                            refetchProfitRolling();
                            loadDashboardData();
                        }}
                        sx={{
                            cursor: 'pointer',
                            borderRadius: '50%',
                            backgroundColor: '#e0e0e0',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                                backgroundColor: '#d5d5d5',
                            },
                        }}
                    >
                        <RefreshIcon />
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );

    return (
        <div>
            <DashboardTitleBar unitName={user?.currentBusinessUnit?.name} />
            {dateFilterRow}
            
            {/* Summary Statistics Cards */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Total Locations"
                            value={stats.totalLocations}
                            icon={<LocationOnIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Total Assets"
                            value={stats.totalAssets}
                            icon={<PointOfSaleIcon />}
                            subtitle={`GOA: ${stats.assetBreakdown.goa}, TRK: ${stats.assetBreakdown.trk}, ATM: ${stats.assetBreakdown.atm}, CTM: ${stats.assetBreakdown.ctm}`}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Total Revenue"
                            value={`$${stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon={<AttachMoneyIcon />}
                            trend={stats.revenueChange !== 0 ? {
                                value: stats.revenueChange,
                                label: 'vs last month'
                            } : undefined}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Recent Visits"
                            value={stats.recentVisits}
                            icon={<EventIcon />}
                            subtitle="Last 7 days"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Active Outages"
                            value={stats.activeOutages}
                            icon={<WarningIcon />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            title="Avg Daily Revenue"
                            value={`$${stats.avgDailyRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon={<TrendingUpIcon />}
                        />
                    </Grid>
                    {stats.topLocation && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Top Location"
                                value={stats.topLocation.name}
                                icon={<LocationOnIcon />}
                                subtitle={`$${stats.topLocation.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Charts Row */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        {renderRollingAverageGraph()}
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        {renderGoaPieChart()}
                    </Grid>
                </Grid>
            </Box>

            {/* Asset Distribution and Performance Metrics */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        {renderAssetDistributionChart()}
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Top 5 Locations by Revenue
                                </Typography>
                                <List>
                                    {stats.topLocations.map((loc, index) => (
                                        <React.Fragment key={loc.name}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={`${index + 1}. ${loc.name}`}
                                                    secondary={`$${loc.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                />
                                            </ListItem>
                                            {index < stats.topLocations.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Recent Activity
                                </Typography>
                                <List>
                                    {locationVisits.slice(0, 5).map((visit: any, index: number) => (
                                        <React.Fragment key={visit.id || index}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={visit.locationName || 'Unknown Location'}
                                                    secondary={dayjs(visit.date).format('MMM DD, YYYY')}
                                                />
                                            </ListItem>
                                            {index < Math.min(4, locationVisits.length - 1) && <Divider />}
                                        </React.Fragment>
                                    ))}
                                    {locationVisits.length === 0 && (
                                        <ListItem>
                                            <ListItemText primary="No recent visits" />
                                        </ListItem>
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
}

export default function Dashboard(props: any) {
    const { user, isLoading } = useAuth();
    const { roles } = useAuthData();
    if (isLoading) {
        return <Loading loading={true} noBackdrop />;
    }
    if (isTomsVendingContext(user)) {
        if (isReportOnlyUser(roles)) {
            return <Navigate to="/app/toms-vending/reports" replace />;
        }
        return <TomsVendingDashboardPage />;
    }
    if (isInventoryAuditorContext(user)) {
        return <InventoryAuditorDashboardPage />;
    }
    return <SouthernMintDashboard {...props} />;
}
